import React, { useRef, useState } from "react";
import { MultiSelect } from "@progress/kendo-react-dropdowns";
import { Checkbox } from "@progress/kendo-react-inputs";
import useOnClickOutside from "use-onclickoutside";
import { icon, label } from "../../../../../config";

/**
 * Multi-select dropdown renderer component
 * @param {Object} props - Component props
 * @param {Object} props.element - Element object
 * @param {Array} props.ddValue - Current dropdown values
 * @param {Array} props.filteredOptions - Filtered options array
 * @param {boolean} props.showResponses - Whether showing responses mode
 * @param {Array} props.selectedValue - Selected values in response mode
 * @param {Object} props.defaultValue - Default value
 * @param {boolean} props.showValidationError - Whether to show validation error
 * @param {boolean} props.hasChoosen - Whether user has made a choice
 * @param {boolean} props.required - Whether the field is required
 * @param {Function} props.handleFilterChange - Filter change handler
 * @param {Function} props.onChange - Change handler
 * @param {Function} props.setDdValue - Set dropdown value function
 * @param {Function} props.setHasChoosen - Set has chosen function
 * @returns {JSX.Element} Multi-select dropdown component
 * @author Abhishek Dhillon
 */
const MultiSelectDropdown = ({
  element,
  ddValue,
  filteredOptions,
  showResponses,
  selectedValue,
  defaultValue,
  showValidationError,
  hasChoosen,
  required,
  handleFilterChange,
  resetFilter,
  onChange,
  setDdValue,
  setHasChoosen,
}) => {
  const { properties = {} } = element;
  const { placeholder = "Select options" } = properties;
  const [multiSelectObj, setMultiSelectObj] = useState({
    open: false,
  });
  const [filterValue, setFilterValue] = useState("");
  const multiSelectRef = useRef(null);

  /**
   * Close the dropdown when clicking outside
   */
  useOnClickOutside(multiSelectRef, (e) => {
    if (!multiSelectRef.current?.contains(e.target) && !e.target.closest(".k-popup")) {
      setMultiSelectObj({ ...multiSelectObj, open: false });
      setFilterValue("");
      resetFilter && resetFilter();
    }
  });

  /**
   * Render the item for the multi select dropdown
   */
  const multiSelectItemRender = (li, itemProps) => {
    const { dataItem } = itemProps;
    const isSelected = Array.isArray(ddValue) ? ddValue?.findIndex((val) => val?.value === dataItem?.value) !== -1 : false;
    const itemChildren = (
      <span
        className='d-flex align-items-center justify-content-between w-100'
        title={dataItem?.label}
        key={dataItem?.value}
        id={`multi-select-item-${dataItem?.value}`}>
        <span className='d-flex align-items-center'>
          <Checkbox
            key={dataItem?.value}
            size='medium'
            id={`workflow-variable-checkbox-${dataItem?.id}`}
            checked={isSelected}
            label={dataItem?.label}
            name={dataItem?.label}
            value={dataItem?.value}
          />
        </span>
      </span>
    );

    const handleClick = (e, dataItem) => {
      let isChecked = ddValue?.find((item) => item?.value === dataItem?.value);
      if (isChecked) {
        let result = ddValue?.filter((item) => item?.value !== dataItem?.value);
        onChange && onChange(result);
        setDdValue(result);
      } else {
        let result = [...ddValue, { ...dataItem, group: "Selected Options" }];
        onChange && onChange(result);
        setDdValue(result);
      }
    };

    return React.cloneElement(
      li,
      {
        ...li.props,
        onClick: (e) => handleClick(e, dataItem),
      },
      itemChildren
    );
  };

  /**
   * Handle the removal of a tag
   */
  const handleTagRemove = (tagData, e) => {
    let newDdValue;
    if (Array.isArray(tagData)) {
      const valuesToRemove = tagData.map((item) => item.value);
      newDdValue = ddValue.filter((item) => !valuesToRemove.includes(item.value));
    } else {
      newDdValue = ddValue.filter((item) => item.value !== tagData?.value);
    }
    onChange && onChange(newDdValue);
    setDdValue(newDdValue);
    setHasChoosen(true);
  };

  /**
   * Render the footer for the multi select dropdown
   */
  const footerRender = <div className='text-right'>Total Selected: {ddValue?.length}</div>;

  /**
   * Render the tag for the multi select dropdown
   */
  const tagRender = (tagData, li) => {
    return React.cloneElement(
      li,
      {
        ...li.props,
        onRemove: (e) => handleTagRemove(tagData.data, e),
        removable: !showResponses,
      },
      [<span title={tagData?.text}>{tagData?.text}</span>]
    );
  };

  /**
   * Render the multi select dropdown
   */
  return (
    <div className='d-flex w-100' ref={multiSelectRef}>
      <MultiSelect
        textField='label'
        valueField='value'
        groupField='group'
        value={showResponses ? selectedValue : ddValue}
        defaultValue={showResponses ? defaultValue : undefined}
        data={[...ddValue, ...filteredOptions?.filter((option) => !ddValue?.some((item) => item.value === option.value))]}
        disabled={showResponses}
        className={`dropdown-input align-items-center ${showValidationError && !hasChoosen ? "k-invalid" : ""}`}
        required={required}
        filterable={true}
        filter={filterValue}
        onFilterChange={(event) => {
          setFilterValue(event.filter?.value || "");
          handleFilterChange(event);
        }}
        itemRender={multiSelectItemRender}
        tagRender={tagRender}
        tags={
          showResponses
            ? Array.isArray(selectedValue)
              ? selectedValue?.map((item) => ({ text: item?.label, data: item }))
              : []
            : ddValue?.length > 3
            ? [{ text: `${ddValue?.length} options selected`, data: ddValue }]
            : ddValue?.map((item) => ({ text: item?.label, data: item }))
        }
        opened={multiSelectObj?.open}
        onOpen={() => setMultiSelectObj({ ...multiSelectObj, open: true })}
        onClose={() => {
          setFilterValue("");
          resetFilter && resetFilter();
        }}
        footer={footerRender}
        autoClose={false}
        allowCustom={false}
        prefix={<span className='ml-2'>{icon.DROPDOWN}</span>}
        placeholder={placeholder}
        rounded={"small"}
      />
      {required && <span className='required-asterisk text-danger ms-1 ml-1'>*</span>}
    </div>
  );
};

export default MultiSelectDropdown;
